import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SendIcon from '@mui/icons-material/Send';

import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import FileDropzoneField from '@/shared/components/ui/FileDropzoneField';
import {
  useAgregarComentarioMasivo,
  useAgregarComentarioMasivoConArchivos,
  useFetchMasivoArchivos,
  useGetHistorialMasivo,
  useGetMasivo,
  usePausarMasivo,
  usePausarMasivoConArchivos,
  useReactivarMasivo,
  useResolverMasivo,
  useResolverMasivoConArchivos,
  descargarArchivoMasivo,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-masivos.actions';
import {
  useFetchTicketCategorias,
  useFetchTicketSubcategorias,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import {
  ITicketMasivo,
  ITicketMasivoHistorial,
  TipoAccionMasivo,
} from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';
import ListaClientesAfectados from '../components/ListaClientesAfectados';
import PausarMasivoDialog from '../components/PausarMasivoDialog';

const ESTADO_CHIP_COLOR: Record<
  string,
  'error' | 'warning' | 'success' | 'default'
> = {
  activo: 'error',
  pausado: 'warning',
  en_reparacion: 'warning',
  resuelto: 'success',
};

const ESTADO_LABEL: Record<string, string> = {
  activo: 'Activo',
  pausado: 'Pausado',
  en_reparacion: 'En Reparacion',
  resuelto: 'Resuelto',
};

const ACCION_LABEL: Record<TipoAccionMasivo, string> = {
  CREACION: 'Creacion',
  PAUSA: 'Pausa',
  REACTIVACION: 'Reactivacion',
  COMENTARIO: 'Comentario',
  RESOLUCION: 'Resolucion',
};

const ACCION_COLOR: Record<TipoAccionMasivo, 'default' | 'primary' | 'warning' | 'success' | 'info' | 'error'> = {
  CREACION: 'primary',
  PAUSA: 'warning',
  REACTIVACION: 'success',
  COMENTARIO: 'info',
  RESOLUCION: 'success',
};

const NIVEL_LABEL: Record<string, string> = {
  olt: 'OLT',
  troncal: 'Troncal (Caja Primaria)',
  nap: 'NAP',
  router: 'Router',
};

const returnUrl = '/tickets-soporte-cliente/masivos';

const getElementosAfectados = (masivo: any): string[] => {
  // Combina lista nueva + singular legacy, dedup. Retorna los codigos
  // seleccionados para el nivel de afectacion del masivo.
  const pickList = (list?: string[], singular?: string | null): string[] => {
    const set = new Set<string>();
    if (Array.isArray(list)) list.forEach((x) => { if (x) set.add(x); });
    if (singular) set.add(singular);
    return Array.from(set);
  };

  switch (masivo.nivel_afectacion) {
    case 'olt':
      return pickList(masivo.olts_afectadas, masivo.olt_afectada);
    case 'troncal':
      return pickList(masivo.troncales_afectadas, masivo.troncal_afectada);
    case 'nap':
      return pickList(masivo.naps_afectadas);
    case 'router':
      return pickList(masivo.routers_afectados, masivo.router_afectado);
    default:
      return [];
  }
};

const DetalleMasivoPage: React.FC = () => {
  const { masivoId } = useParams<{ masivoId: string }>();
  const navigate = useNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [observacionResolucion, setObservacionResolucion] = useState('');
  const [subcategoriaCierreId, setSubcategoriaCierreId] = useState('');
  const [archivosResolucion, setArchivosResolucion] = useState<File[]>([]);
  const [isPausarOpen, setIsPausarOpen] = useState(false);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [archivosComentario, setArchivosComentario] = useState<File[]>([]);

  // Subcategorias de cierre disponibles para masivos: viven bajo la
  // categoria con codigo 'motivos_cierre' + aplica_masivos=true.
  const { data: catsCierreData } = useFetchTicketCategorias(undefined, true);
  const categoriaCierre: any = ((catsCierreData?.data as any)?.data ?? [])
    .find((c: any) => c.codigo === 'motivos_cierre');
  const { data: subcatsCierreData } = useFetchTicketSubcategorias(
    categoriaCierre?.id_ticket_categoria,
    true,
  );
  const subcategoriasCierre: any[] =
    (subcatsCierreData?.data as any)?.data ?? [];

  const { data: masivoResponse, isLoading } = useGetMasivo(masivoId ?? '');
  const { data: historialResponse } = useGetHistorialMasivo(masivoId ?? '');
  const { data: archivosResponse } = useFetchMasivoArchivos(masivoId ?? '');
  const resolverMutation = useResolverMasivo();
  const resolverConArchivosMutation = useResolverMasivoConArchivos();
  const pausarMutation = usePausarMasivo();
  const pausarConArchivosMutation = usePausarMasivoConArchivos();
  const reactivarMutation = useReactivarMasivo();
  const comentarioMutation = useAgregarComentarioMasivo();
  const comentarioConArchivosMutation = useAgregarComentarioMasivoConArchivos();

  // Agrupa los archivos del masivo por historial_id para renderizar
  // los adjuntos dentro de cada card del timeline.
  const archivosFlat: any[] = (archivosResponse as any)?.data ?? [];
  const archivosPorHistorial = new Map<string, any[]>();
  for (const a of archivosFlat) {
    if (!a.historial_id) continue;
    const arr = archivosPorHistorial.get(a.historial_id) ?? [];
    arr.push(a);
    archivosPorHistorial.set(a.historial_id, arr);
  }

  const historial: ITicketMasivoHistorial[] =
    ((historialResponse as any)?.data?.data as ITicketMasivoHistorial[])
    ?? ((historialResponse as any)?.data as ITicketMasivoHistorial[])
    ?? [];

  const masivo: ITicketMasivo | undefined = (masivoResponse?.data ?? masivoResponse) as ITicketMasivo | undefined;

  const handleResolver = async () => {
    if (!masivoId) return;

    const onSuccess = () => {
      setIsDialogOpen(false);
      setObservacionResolucion('');
      setSubcategoriaCierreId('');
      setArchivosResolucion([]);
      navigate(returnUrl);
    };

    if (archivosResolucion.length > 0) {
      resolverConArchivosMutation.mutate(
        {
          id: masivoId,
          observacion: observacionResolucion,
          subcategoriaCierreId: subcategoriaCierreId || null,
          archivos: archivosResolucion,
        },
        { onSuccess },
      );
    } else {
      resolverMutation.mutate(
        {
          id: masivoId,
          data: {
            observacion_resolucion: observacionResolucion,
            subcategoria_cierre_id: subcategoriaCierreId || null,
          },
        },
        { onSuccess },
      );
    }
  };

  const handleConfirmarPausa = (motivo: string, archivosPausa: File[]) => {
    if (!masivoId) return;
    const onSettled = () => setIsPausarOpen(false);
    if (archivosPausa.length > 0) {
      pausarConArchivosMutation.mutate(
        { id: masivoId, motivo, archivos: archivosPausa },
        { onSettled },
      );
    } else {
      pausarMutation.mutate(
        { id: masivoId, motivo },
        { onSettled },
      );
    }
  };

  const handleReactivar = () => {
    if (!masivoId) return;
    reactivarMutation.mutate({ id: masivoId });
  };

  const handleAgregarComentario = () => {
    if (!masivoId) return;
    const texto = nuevoComentario.trim();
    const tieneArchivos = archivosComentario.length > 0;
    if (!texto && !tieneArchivos) return;

    const onSuccess = () => {
      setNuevoComentario('');
      setArchivosComentario([]);
    };

    if (tieneArchivos) {
      comentarioConArchivosMutation.mutate(
        { id: masivoId, comentario: texto, archivos: archivosComentario },
        { onSuccess },
      );
    } else {
      comentarioMutation.mutate(
        { id: masivoId, comentario: texto },
        { onSuccess },
      );
    }
  };

  if (isLoading) {
    return (
      <Paper elevation={2} sx={{ padding: 3, borderRadius: 2 }}>
        <Typography>Cargando...</Typography>
      </Paper>
    );
  }

  if (!masivo) {
    return (
      <Paper elevation={2} sx={{ padding: 3, borderRadius: 2 }}>
        <Typography>No se encontro el caso masivo.</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(returnUrl)}
          sx={{ mt: 2 }}
        >
          Volver
        </Button>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={2} sx={{ padding: 3, mb: 2, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(returnUrl)}
            >
              Volver
            </Button>
            <Typography variant="h4">Detalle Caso Masivo</Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            {masivo.estado_masivo === 'activo' && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<PauseCircleIcon />}
                onClick={() => setIsPausarOpen(true)}
              >
                Pausar
              </Button>
            )}
            {masivo.estado_masivo === 'pausado' && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<PlayCircleIcon />}
                onClick={handleReactivar}
                disabled={reactivarMutation.isPending}
              >
                Reactivar
              </Button>
            )}
            {masivo.estado_masivo !== 'resuelto' && (
              <Button
                variant="contained"
                color="success"
                onClick={() => setIsDialogOpen(true)}
              >
                Resolver Masivo
              </Button>
            )}
          </Stack>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Codigo
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {masivo.codigo}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Estado
            </Typography>
            <Chip
              label={ESTADO_LABEL[masivo.estado_masivo] ?? masivo.estado_masivo}
              color={ESTADO_CHIP_COLOR[masivo.estado_masivo] ?? 'default'}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Clientes Afectados
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {masivo.clientes_afectados_count}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Nivel de Afectacion
            </Typography>
            <Chip
              label={NIVEL_LABEL[masivo.nivel_afectacion] ?? masivo.nivel_afectacion ?? 'NAP'}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Grid>

          {/* Clasificacion */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">Tipo</Typography>
            <Typography variant="body2" fontWeight={500}>
              {(masivo as any).tipo?.nombre ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">Categoria</Typography>
            <Typography variant="body2" fontWeight={500}>
              {(masivo as any).categoria?.nombre ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary">Subcategoria</Typography>
            <Typography variant="body2" fontWeight={500}>
              {(masivo as any).subcategoria?.nombre ?? '-'}
            </Typography>
          </Grid>
          {(masivo as any).subcategoria_cierre && (
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Subcategoria de Cierre
              </Typography>
              <Typography variant="body2" fontWeight={500} color="success.main">
                {(masivo as any).subcategoria_cierre.nombre}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {`Elementos Afectados (${getElementosAfectados(masivo).length})`}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {getElementosAfectados(masivo).slice(0, 30).map((codigo, idx) => (
                <Chip key={`${codigo}-${idx}`} label={codigo} size="small" variant="outlined" />
              ))}
              {getElementosAfectados(masivo).length > 30 && (
                <Chip
                  label={`+${getElementosAfectados(masivo).length - 30} mas`}
                  size="small"
                  color="default"
                />
              )}
              {getElementosAfectados(masivo).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Sin elementos seleccionados
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
              Descripcion
            </Typography>
            <Typography variant="body1">{masivo.descripcion}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" sx={{ mb: 2 }}>
          Clientes Afectados
        </Typography>
        <ListaClientesAfectados masivoId={masivoId!} />

        <Divider sx={{ my: 3 }} />

        {/* Historial / Trazabilidad */}
        <Typography variant="h5" sx={{ mb: 2 }}>
          Historial
        </Typography>

        {masivo.estado_masivo !== 'resuelto' && (
          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <TextField
              label="Agregar comentario"
              placeholder="Escriba un comentario (opcional si adjunta archivos)"
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              size="small"
              value={nuevoComentario}
              onChange={(e) => setNuevoComentario(e.target.value)}
            />
            <FileDropzoneField
              value={archivosComentario}
              onChange={setArchivosComentario}
              helperText="Adjunte evidencias del comentario (opcional). Imágenes, PDF, ZIP, Excel — máx. 50 MB c/u"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleAgregarComentario}
                disabled={
                  (!nuevoComentario.trim() && archivosComentario.length === 0)
                  || comentarioMutation.isPending
                  || comentarioConArchivosMutation.isPending
                }
              >
                {archivosComentario.length > 0
                  ? `Enviar (${archivosComentario.length})`
                  : 'Enviar'}
              </Button>
            </Box>
          </Stack>
        )}

        {historial.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin registros de actividad.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {historial.map((h) => {
              const archivosH = archivosPorHistorial.get(h.id_ticket_masivo_historial) ?? [];
              return (
                <Box
                  key={h.id_ticket_masivo_historial}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    bgcolor: 'background.default',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Chip
                      label={ACCION_LABEL[h.tipo_accion] ?? h.tipo_accion}
                      size="small"
                      color={ACCION_COLOR[h.tipo_accion] ?? 'default'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {h.fecha ? new Date(h.fecha).toLocaleString('es-EC') : ''}
                    </Typography>
                    {h.usuario_nombre && (
                      <Typography variant="caption" color="text.secondary">
                        - {h.usuario_nombre}
                      </Typography>
                    )}
                    {h.estado_anterior && h.estado_nuevo && h.estado_anterior !== h.estado_nuevo && (
                      <Typography variant="caption" color="text.secondary">
                        {`${h.estado_anterior} -> ${h.estado_nuevo}`}
                      </Typography>
                    )}
                  </Stack>
                  {h.detalle && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {h.detalle}
                    </Typography>
                  )}
                  {archivosH.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {archivosH.map((a) => (
                        <Chip
                          key={a.id_masivo_archivo}
                          onClick={() => descargarArchivoMasivo(a.id_masivo_archivo)}
                          clickable
                          label={a.nombre_original}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      <PausarMasivoDialog
        open={isPausarOpen}
        masivo={masivo}
        isLoading={pausarMutation.isPending || pausarConArchivosMutation.isPending}
        onClose={() => setIsPausarOpen(false)}
        onConfirm={handleConfirmarPausa}
      />

      <ScrollableDialogProps
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCloseIcon={() => setIsDialogOpen(false)}
        title="Resolver Masivo"
        contentNode={
          <Box sx={{ minWidth: 460 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ingrese una observacion para la resolucion del caso masivo.
            </Typography>
            <Stack spacing={2}>
              {/* Subcategoria de cierre: opcional. Pool = subcategorias
                  bajo motivos_cierre con aplica_masivos=true. */}
              <TextField
                select
                fullWidth
                size="small"
                label="Subcategoria de cierre"
                value={subcategoriaCierreId}
                onChange={(e) => setSubcategoriaCierreId(e.target.value)}
                helperText={
                  subcategoriasCierre.length === 0
                    ? 'No hay subcategorias de cierre marcadas para masivos'
                    : ' '
                }
              >
                <MenuItem value="">-- Sin especificar --</MenuItem>
                {subcategoriasCierre.map((s: any) => (
                  <MenuItem key={s.id_ticket_subcategoria} value={s.id_ticket_subcategoria}>
                    {s.nombre}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Observacion de Resolucion"
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                value={observacionResolucion}
                onChange={(e) => setObservacionResolucion(e.target.value)}
              />
              <FileDropzoneField
                value={archivosResolucion}
                onChange={setArchivosResolucion}
                helperText="Adjunte evidencias de la resolución (opcional). Imágenes, PDF, ZIP, Excel — máx. 50 MB c/u"
              />
            </Stack>
          </Box>
        }
        showConfirmBtn
        confirmTextBtn={
          archivosResolucion.length > 0
            ? `Confirmar Resolucion (${archivosResolucion.length})`
            : 'Confirmar Resolucion'
        }
        confirmBtnColor="success"
        confirmVariantBtn="contained"
        confirmDisabled={
          !observacionResolucion.trim()
          || resolverMutation.isPending
          || resolverConArchivosMutation.isPending
        }
        onConfirm={handleResolver}
      />
    </>
  );
};

export default DetalleMasivoPage;
